import { MouseEvent, MouseEventHandler, useCallback, useMemo } from 'react';
import { getTimeAgoText } from '../../utils/dates';
import { Player } from '../../types/players';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';

type TProps = {
  title: string;
  body: string;
  notificationData: any;
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onPlayerClick?: (
    e: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>,
    player: Player
  ) => void;
};

const NotificationToast = ({
  title,
  body,
  notificationData,
  className,
  onClick,
  onPlayerClick,
}: TProps) => {
  const { t, i18n } = useTranslation();

  dayjs.locale(i18n.language);

  const containerClass = useMemo(() => {
    let _className = 'flex flex-col';

    if (className) {
      _className += ` ${className}`;
    }

    return _className;
  }, [className]);

  const renderUserImg = useCallback(() => {
    if (notificationData.sender?.imageUrl) {
      const { imageUrl } = notificationData.sender;

      if (imageUrl) {
        return (
          <img
            src={imageUrl}
            alt="profile"
            className="mr-4 rounded-full w-[36px] h-[36px] object-cover flex-shrink-0"
            width={36}
            height={36}
          />
        );
      }
    }

    return (
      <div className="mr-4 rounded-full w-[36px] h-[36px] border border-gray-400 bg-mainBlack flex-shrink-0"></div>
    );
  }, [notificationData]);

  const renderNotificationText = useCallback(() => {
    const paragraphClassName = 'text-white text-sm whitespace-pre-wrap';

    const { targetType, targetAction, reservation } = notificationData;
    const { field, date, start, end, recurringEndDate, recurringStartDate } = reservation || {
      field: { name: ' ', complexName: ' ' },
      date: ' ',
      start: ' ',
      end: ' ',
    };

    const parsedDate = date ? dayjs(date).format('dddd DD.MM.YYYY') : date;

    if (targetType === 'RESERVATION') {
      switch (targetAction) {
        case 'CREATE':
        case 'CANCEL':
          return (
            <>
              <p className={paragraphClassName}>
                <span>{`${t(
                  COMMON_TRANSLATE_KEYS.COMPLEX
                ).toLowerCase()}`}</span>
                <b> {field.complexName}</b>
              </p>
              <p className={paragraphClassName}>
                <span>{`${t(COMMON_TRANSLATE_KEYS.FIELD).toLowerCase()}`}</span>
                <b> {field.name}</b>
              </p>
              {!recurringEndDate && (
                <p className={paragraphClassName}>
                  <b>{parsedDate}</b>
                </p>
              )}
              {recurringStartDate && (
                <p className={paragraphClassName}>
                <span>{t(COMMON_TRANSLATE_KEYS.FROM)}</span>
                <b> {dayjs(recurringStartDate).format('dddd DD.MM.YYYY')}</b>
              </p>
              )}
              {recurringEndDate && (
                <p className={paragraphClassName}>
                  <span>{t(COMMON_TRANSLATE_KEYS.UNTIL)}</span>
                  <b> {dayjs(recurringEndDate).format('dddd DD.MM.YYYY')}</b>
                </p>
              )}
              <p className={paragraphClassName}>
                <b>{`${start} - ${end}`}</b>
              </p>
            </>
          );
      }
    }

    return <p className={paragraphClassName}>{body}</p>;
  }, [notificationData, body, t]);

  return (
    <div className={containerClass} onClick={onClick}>
      <div className="flex flex-row">
        {renderUserImg()}
        <div className="flex flex-col text-start overflow-hidden">
          <span
            className={`text-white font-bold text-ellipsis overflow-hidden ${
              onPlayerClick ? 'hover:underline' : ''
            }`}
            onClick={(e) =>
              onPlayerClick && onPlayerClick(e, notificationData.sender)
            }
          >
            {notificationData.sender?.name}
          </span>
          <p className="text-textGray whitespace-pre-wrap">{title.toUpperCase()}</p>
          {renderNotificationText()}
          <p className="text-textGray text-sm whitespace-pre-wrap text-end">
            {getTimeAgoText(notificationData.createdAt, true)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default NotificationToast;
